import classNames from 'classnames';
import React from 'react';

import {DiscoveryItemImpressionEvent, discoveryTracker} from '@udemy/browse-event-tracking';
import {TrackImpression} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {VoucherCard} from '@udemy/react-certification-preparation';
import {CertificationVoucherBuyable, ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

import {getConfig} from 'src/features/cart/config';

import {Actions} from './actions.react-component';
import styles from './certification-voucher-item.module.less';
import {Price} from './price.react-component';
import {ActionHandlers, ActionSupportedLayoutsProps, CartItem} from './types';

/** Upsell buyables include issuerName from GQL; cart API buyables may only have certification_provider. */
type CertificationVoucherBuyableWithIssuer = CertificationVoucherBuyable & {
    issuerName?: string | null;
};

/**
 * DiscoveryItemImpressionEvent requires a valid discovery item type (e.g. "course").
 * Cart API buyables include _class for courses but not certification vouchers.
 */
function toDiscoveryImpressionItem(buyable: CertificationVoucherBuyableWithIssuer) {
    return {
        ...buyable,
        _class: buyable._class ?? 'course',
    };
}

export interface CertificationVoucherItemProps {
    item: UdemyShoppingItem;
    index: number;
    listName: string;
    performanceMarkName?: string;
    actions: ActionHandlers;
    actionsLayout?: ActionSupportedLayoutsProps;
    usesLatestDiscount?: (item: CartItem) => boolean;
    /** When provided, replaces the default Actions component. Used by upsell rows to show "Add to Cart" instead of list-management actions. */
    renderActions?: () => React.ReactNode;
}

export const CertificationVoucherItem: React.FC<CertificationVoucherItemProps> = ({
    item,
    index,
    usesLatestDiscount = () => false,
    actionsLayout = 'horizontal',
    renderActions,
    ...rest
}) => {
    const {buyable, list_price: listPrice, purchase_price: purchasePrice} = item;
    const {gettext, interpolate, ngettext} = useI18n();
    const config = getConfig(gettext, interpolate, ngettext);
    const voucherBuyable = buyable as CertificationVoucherBuyableWithIssuer;
    const isMobile = useMatchMedia('mobile-max');

    function trackImpression() {
        discoveryTracker.trackDiscoveryImpression(
            {item: toDiscoveryImpressionItem(voucherBuyable)},
            {
                backendSource: DiscoveryItemImpressionEvent.backendSourceOptions.SHOPPING_CARTS,
                index,
            },
        );
    }

    const trackClick = () => {
        discoveryTracker.trackDiscoveryItemClickEvent(
            {item: toDiscoveryImpressionItem(voucherBuyable)},
            {
                backendSource: DiscoveryItemImpressionEvent.backendSourceOptions.SHOPPING_CARTS,
                index,
            },
        );
    };

    const {providerLabel, providerNames, validityText, examFormatText} =
        config.strings.certificationVoucher;
    const providerName = voucherBuyable.issuerName
        ? providerLabel(voucherBuyable.issuerName)
        : providerNames[voucherBuyable.certification_provider];
    const newTabProps = !isMobile ? {target: '_blank' as const, rel: 'noopener'} : {};

    return (
        <TrackImpression trackFunc={trackImpression}>
            <div
                className={classNames(styles.container, {
                    [styles['with-inline-actions']]: actionsLayout === 'vertical',
                })}
            >
                <div className={styles['voucher-image']}>
                    <VoucherCard
                        logoSrc={voucherBuyable.image_100x100}
                        discountPercentage={0}
                        size="medium"
                    />
                </div>

                <div className={styles.header}>
                    <h3 className={classNames('ud-heading-md', styles['voucher-title'])}>
                        <a href={voucherBuyable.url} onClick={trackClick} {...newTabProps}>
                            {voucherBuyable.title}
                        </a>
                    </h3>
                    {providerName && (
                        <span className={classNames('ud-text-xs', styles['voucher-provider'])}>
                            {providerName}
                        </span>
                    )}
                </div>

                <div className={styles.meta}>
                    <span className="ud-text-xs">
                        {validityText(voucherBuyable.expiration_date_in_months)}
                    </span>
                    <span className={classNames('ud-text-xs', styles['meta-separator'])}>•</span>
                    <span className="ud-text-xs">{examFormatText}</span>
                </div>

                {renderActions ? (
                    renderActions()
                ) : (
                    <Actions
                        item={item}
                        layout={actionsLayout}
                        showSaveForLaterAction={true}
                        {...rest}
                    />
                )}

                <Price
                    isUB={false}
                    buyable={voucherBuyable}
                    listPrice={listPrice}
                    purchasePrice={purchasePrice}
                    currentDiscount={item.current_discount}
                    shouldUseLatestDiscountStyles={usesLatestDiscount(item)}
                />
            </div>
        </TrackImpression>
    );
};
